/**
 * THE ACCESSORIES THE FLOOR COMES WITH — the switchboard under the product.
 *
 * Jacky, 19 September, on the real O-00000004 (Kieren Halikakis): *"trying to do proposal but wont allow
 * adding trims or able selecting type of underlay or moisture barrier under the product switch like
 * previous platform"*.
 *
 * He was right, and the reason was underneath the screen: `/api/pricing-base` read `category = Main
 * Product` and nothing else, so the face never had an underlay to choose, a trim to add, or a moisture
 * barrier to switch on. The accessories did exist — the Work panel seeded three generic lines with a
 * free-text "colour" and no rate — but a generic line is not a choice of type, and nothing could be added
 * that the drawing had not implied.
 *
 * This is the platform's own shape, in AIW's model. Under each product line:
 *
 *   · repeatable trim and underlay rows; one switch for each other FAMILY the catalogue holds — underlay, moisture barrier, scotia/edging, trims,
 *     adhesive, levelling, stair fittings;
 *   · the TYPE inside it (2mm Green UD or 2mm Silver UD; U Channel or Uni Cover or T-Module), which is a
 *     catalogue product;
 *   · the supplier / spec option inside the type (PAF, PRF, TDK, WDW, 8mm, 12mm) — the second level the
 *     platform showed and this picker never had;
 *   · the quantity, seeded from what the drawing already measured, and his to change.
 *
 * A switch ticked here is BOUND to the product, which is the rule Jacky gave on 18 September: *"remeber
 * the accessories that is binded to main product, customer will see indent line showing its included in
 * the price"*. So it carries no customer figure of its own; it is part of that product's scope. An
 * accessory that stands alone — priced as its own line — is still added from the Work panel.
 */
import {useMemo,useState} from 'react';
import {manualTrimChargingPatch,newRepeatedAccessory,accessoryScopePatch,floorAccessoryContext,floorAccessoryFamilies,floorAccessoryDefault,missingDrawnTrims,drawnTrimRows} from '@artemis-group/aiw-contracts';
import type {ProposalAccessory, ProposalSelection} from '@artemis-group/aiw-contracts';
import type {AccessoryType, ReviewProduct} from '../lib/proposal-pricing';
import type {WorkMeasurements} from './proposal-work';
import {useT} from '../lib/i18n';
import {accessoryKindLabel} from '../lib/accessory-kinds';
import {inputCls} from './job-bits';
import {NumberField} from './number-field';
import {money} from '../lib/format';
import {accessoryVariantLabel,accessoryMeasurementUnit,scopedAreaQuantity} from '../lib/proposal-accessory-scope';
import {authHeaders,fetchImpl} from '../lib/api';
import {env} from '../lib/env';

/**
 * WHAT A FAMILY IS MEASURED IN — the drawing's own number, never a typed guess.
 *
 * Underlay and a moisture barrier cover only scoped assessment floor area. Product overrides and
 * flooring wastage do not expand their scope. Scotia follows the drawn skirting run and trims the trims
 * the canvas has already drawn; the stair fittings follow the steps. Nothing here invents a figure — when
 * the drawing cannot answer, the quantity starts at zero and it is his.
 */
function seedQuantity(kind: string, selection: ProposalSelection, m: WorkMeasurements | undefined): number {
  const round = (n: number) => Math.round(n * 100) / 100;
  if (kind === 'underlay' || kind === 'moisture_barrier' || kind === 'adhesive' || kind === 'levelling')
    return scopedAreaQuantity(selection,m,'');
  if (kind === 'scotia'||kind==='smoothedge') return round(m?.skirtingRunM ?? 0);
  if (kind === 'trims') return round(m?.trimRunM ?? 0);
  if (kind === 'stair') return round(m?.stairSteps ?? 0);
  return 0;
}

/** The accessory this floor already carries for a family — a bound one, a site-wide one, or an old seed. */
function accessoryFor(accessories: ProposalAccessory[], kind: string, productId: string) {
  return accessories.find((a) => a.kind === kind && a.bound_to === productId)
    ?? accessories.find((a) => a.kind === kind && !a.bound_to)
    ?? accessories.find((a) => a.id === kind)
    ?? accessories.find((a) => a.id === `${kind}:${productId}`);
}

/** The option a type starts on: the catalogue's default, else its first. */
const defaultVariant = (type: AccessoryType) => type.variants.find((v) => v.is_default) ?? type.variants[0] ?? null;

export function AccessorySwitchboard({product, selection, catalogue, accessories, measurements, onChange}: {
  product: ReviewProduct;
  selection: ProposalSelection;
  /** The accessories the catalogue holds, as `/api/pricing-base` sends them. */
  catalogue: AccessoryType[] | null | undefined;
  accessories: ProposalAccessory[];
  measurements?: WorkMeasurements;
  onChange: (next: ProposalAccessory[]) => void;
}) {
  const {t} = useT();
  const carpet = product.pricing.unit === 'LM';
  const {manufacturerId,boardThickness}=floorAccessoryContext(product,selection);
  const pickVariant=(kind:string,type:AccessoryType)=>floorAccessoryDefault(kind,type,boardThickness);
  const [addingDrawn,setAddingDrawn]=useState(false);
  const addResolvedDrawnTrims=async(types:AccessoryType[])=>{
    setAddingDrawn(true);
    let confirmedAccessoryVariantId:string|null=null;
    if(selection.variantId){
      try{
        const response=await fetchImpl(`${env.apiBase}/api/accessory-match/resolve`,{method:'POST',headers:{'content-type':'application/json',...await authHeaders()},body:JSON.stringify({floor_variant_id:selection.variantId,accessory_type:'aluminium'})});
        const body=await response.json();
        if(response.ok&&body.data?.resolution?.state==='confirmed')confirmedAccessoryVariantId=body.data.resolution.accessory_variant_id;
      }catch{/* An unresolved row remains editable; it is never presented as order-ready. */}
    }
    onChange([...accessories,...drawnTrimRows({drawn:measurements?.drawnTrims??[],productId:product.id,accessories,types,boardThickness,confirmedAccessoryVariantId})]);
    setAddingDrawn(false);
  };


  /**
   * THE FAMILIES THIS FLOOR GETS, with the types inside each.
   *
   * A moisture barrier is for a hard floor over a slab; a carpet does not take one. Everything else the
   * catalogue holds is offered — the catalogue is the list, and a floor that never needs trims simply
   * leaves them switched off.
   */
  const families = useMemo(() => floorAccessoryFamilies(catalogue??[],carpet,manufacturerId), [catalogue,carpet,manufacturerId]);

  if (!families.length&&!carpet) return null;

  /** Write one family's row: patch the line it already has, or add it bound to this product. */
  const write = (kind: string, patch: Partial<ProposalAccessory> | null) => {
    const held = accessoryFor(accessories, kind, product.id);
    if (patch === null) {
      if (held) onChange(accessories.filter((a) => a !== held));
      return;
    }
    if (held) onChange(accessories.map((a) => (a === held ? {...a, kind, bound_to: product.id, ...patch} : a)));
    else onChange([...accessories, {
      id: `${kind}:${product.id}`, kind, name: patch.name ?? (kind==='smoothedge'?t('accessory.carpet.smoothedge'):accessoryKindLabel(t, kind)),
      scope: 'site' as const, area_id: '', run_ids: [], colour: null, on: true, bound_to: product.id, quantity_source:'assessment', ...patch,
      unit: patch.unit ?? 'm²', quantity: patch.quantity ?? 0, rate: null, note: '',
      product_id: patch.product_id ?? null, variant_id: patch.variant_id ?? null,
    }]);
  };

  /** Pick a TYPE: the catalogue product, its default option, and the drawing's quantity. */
  const chooseType = (kind: string, type: AccessoryType | undefined) => {
    if (!type) return;
    const variant = pickVariant(kind,type);
    write(kind, {
      on: true, product_id: type.product_id, variant_id: variant?.variant_id ?? null,
      name: type.name, unit: accessoryMeasurementUnit(kind,variant?.unit??type.unit),
      quantity: ['underlay','moisture_barrier','adhesive','levelling'].includes(kind)?scopedAreaQuantity(selection,measurements,accessoryFor(accessories,kind,product.id)?.area_id??''):seedQuantity(kind,selection,measurements), quantity_source:'assessment',
    });
  };

  const addAccessory = (kind:'underlay'|'trims',types: AccessoryType[]) => {
    onChange([...accessories,newRepeatedAccessory({id:crypto.randomUUID(),kind,fallbackName:accessoryKindLabel(t,kind),productId:product.id,selection,accessories,measurements,types,boardThickness})]);
  };

  const manualTrim=(name:'Naplock'|'Ripple Bar')=>accessories.find(a=>a.id===`carpet-trim:${name}:${product.id}`)??accessories.find(a=>a.kind==='trims'&&a.name===name&&a.bound_to===product.id&&a.product_id===null);
  const setManualTrim=(name:'Naplock'|'Ripple Bar',on:boolean)=>{
    const held=manualTrim(name);
    if(!on){onChange(accessories.filter(a=>a!==held));return;}
    if(held)return;
    onChange([...accessories,{id:`carpet-trim:${name}:${product.id}`,kind:'trims',name,scope:'site',area_id:'',run_ids:[],colour:null,on:true,bound_to:product.id,unit:'LM',quantity:0,rate:null,
      quantity_source:'manual',note:'',product_id:null,variant_id:null}]);
  };
  const patchManualTrim=(name:'Naplock'|'Ripple Bar',patch:Partial<ProposalAccessory>)=>onChange(accessories.map(a=>a===manualTrim(name)?{...a,...patch}:a));

  return <div className="mt-2 rounded border bg-muted/20 p-2">
    <p className="text-micro font-medium">{t('accessory.title')}</p>
    <p className="text-micro text-muted-foreground">{t('accessory.hint')}</p>
    <div className="mt-1.5 space-y-1.5">
      {families.map(({kind, types}) => {
        if(kind==='underlay'||kind==='trims'){
          const rows=accessories.filter((a)=>a.kind===kind&&(a.bound_to===product.id||(kind==='trims'&&!a.bound_to&&a.id==='trims')));
          return <div key={kind} className="rounded border bg-background p-2">
            <div className="flex flex-wrap items-center justify-between gap-2"><strong className="text-sm">{accessoryKindLabel(t,kind)}</strong><button type="button" className="text-micro underline" disabled={accessories.length>=60} onClick={()=>addAccessory(kind,types)}>{t(kind==='trims'?'accessory.addTrim':'accessory.addUnderlay')}</button></div>
            {kind==='trims'&&(()=>{
              // The trims the drawing already has (16 Oct 2026): one click puts each drawn profile on this floor, included in
              // its price as every quote has carried them. A row he clears is offered again here, never re-added by itself.
              const missing=missingDrawnTrims(measurements?.drawnTrims??[],product.id,accessories,types);
              if(!missing.length)return null;
              return <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-dashed p-2 text-micro">
                <span>{t('accessory.drawnTrims',{list:missing.map(m=>`${m.trim.profile} ${m.trim.length_m} LM`).join(' · ')})}</span>
                <button type="button" className="underline" disabled={addingDrawn||accessories.length>=60} onClick={()=>void addResolvedDrawnTrims(types)}>{t('accessory.addDrawnTrims')}</button>
              </div>;
            })()}
            <div className="mt-2 space-y-2">{rows.map((row)=>{
              const chosen=types.find((x)=>x.product_id===row.product_id);
              const variant=chosen?.variants.find((v)=>v.variant_id===row.variant_id)??null;
              const floor=row.scope==='area'?measurements?.floors.find((f)=>f.area_id===row.area_id):undefined;
              return <div key={row.id} className="flex flex-wrap items-end gap-2 rounded border p-2">
                <label className="text-micro"><input type="checkbox" checked={row.on} onChange={e=>onChange(accessories.map(a=>a.id===row.id?{...a,on:e.target.checked}:a))}/> {t('commercial.included')}</label>
                <label className="min-w-[180px] flex-1 text-micro">{t('accessory.chooseType')}
                  <select className={`${inputCls} mt-1 w-full`} value={chosen?.product_id??''} onChange={(e)=>{
                    const picked=types.find((x)=>x.product_id===e.target.value);const next=picked?pickVariant(kind,picked):null;
                    onChange(accessories.map((a)=>a.id===row.id?{...a,on:true,name:picked?.name??a.name,product_id:picked?.product_id??null,variant_id:next?.variant_id??null,unit:kind==='trims'?'LM':'m²'}:a));
                  }}><option value="">{t('accessory.chooseType')}</option>{types.map((x)=><option key={x.product_id} value={x.product_id}>{x.name}</option>)}</select>
                </label>
                {!!chosen?.variants.length&&<select aria-label={t('accessory.chooseType')} className={`${inputCls} min-w-[150px]`} value={row.variant_id??''} onChange={(e)=>onChange(accessories.map((a)=>a.id===row.id?{...a,variant_id:e.target.value||null}:a))}> <option value="">{t('accessory.chooseType')}</option>{chosen!.variants.map((v)=><option key={v.variant_id} value={v.variant_id}>{accessoryVariantLabel(v)}</option>)}</select>}
                <label className="text-micro">{t('work.scope')}<select className={`${inputCls} mt-1 block`} value={row.scope==='site'?'site':row.area_id} onChange={(e)=>{
                  const areaId=e.target.value==='site'?'':e.target.value;
                  onChange(accessories.map((a)=>a.id===row.id?{...a,...accessoryScopePatch(a,selection,measurements,areaId)}:a));
                }}><option value="site">{t('work.wholeSite')}</option>{(measurements?.floors??[]).map((f)=><option key={f.area_id} value={f.area_id}>{f.name}</option>)}</select></label>
                <label className="text-micro">{t('accessory.qty')}<span className="mt-1 flex items-center gap-1"><NumberField className={`${inputCls} w-20`} value={row.quantity} onChange={(n)=>onChange(accessories.map((a)=>a.id===row.id?{...a,quantity:n,quantity_source:'manual'}:a))}/><span>{row.unit}</span></span></label>
                {variant?.cost!=null&&<span className="text-micro text-muted-foreground">{t('accessory.carried',{cost:money(variant.cost),unit:variant.unit??chosen?.unit??'m²'})}</span>}
                <button type="button" className="text-micro underline" onClick={()=>onChange(accessories.filter((a)=>a.id!==row.id))}>{t('accessory.clear')}</button>
                {kind==='underlay'&&(floor?.underlay||(!floor&&measurements?.underlay))&&<p className="w-full text-micro text-muted-foreground">{t('accessory.assessmentNeed',{requirement:floor?.underlay??measurements?.underlay??''})}</p>}
              </div>;
            })}</div>
          </div>;
        }
        const held = accessoryFor(accessories, kind, product.id);
        const chosenType = held?.product_id ? types.find((x) => x.product_id === held.product_id) : undefined;
        const variant = chosenType?.variants.find((v) => v.variant_id === held?.variant_id) ?? (chosenType ? defaultVariant(chosenType) : null);
        return <div key={kind} className="flex flex-wrap items-center gap-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!held?.on} onChange={(e) => {
              write(kind, {on: e.target.checked,...(!held?{quantity:seedQuantity(kind,selection,measurements)}:{})});
            }} />
            <span className="w-[104px] shrink-0">{kind==='smoothedge'?t('accessory.carpet.smoothedge'):accessoryKindLabel(t, kind)}</span>
          </label>
          <select className={`${inputCls} min-w-[180px] flex-1`} value={chosenType?.product_id ?? ''}
            aria-label={`${accessoryKindLabel(t, kind)} type`}
            onChange={(e) => chooseType(kind, types.find((x) => x.product_id === e.target.value))}>
            <option value="">{t('accessory.chooseType')}</option>
            {types.map((x) => <option key={x.product_id} value={x.product_id}>{x.name}</option>)}
          </select>
          {(chosenType?.variants.length ?? 0) > 1 && <select className={`${inputCls} min-w-[160px]`}
            value={held?.variant_id ?? variant?.variant_id ?? ''} aria-label={`${accessoryKindLabel(t, kind)} option`}
            onChange={(e) => {
              const picked = chosenType!.variants.find((v) => v.variant_id === e.target.value);
              write(kind, {on: true, variant_id: picked?.variant_id ?? null, unit: accessoryMeasurementUnit(kind,picked?.unit??chosenType!.unit)});
            }}>
            {chosenType!.variants.map((v) => <option key={v.variant_id} value={v.variant_id}>{accessoryVariantLabel(v)}</option>)}
          </select>}
          {held?.on&&['moisture_barrier','adhesive','levelling'].includes(kind)&&<label className="text-micro">{t('work.scope')}<select className={inputCls} value={held.scope==='site'?'site':held.area_id} onChange={e=>{const areaId=e.target.value==='site'?'':e.target.value;write(kind,accessoryScopePatch(held,selection,measurements,areaId));}}><option value="site">{t('work.wholeSite')}</option>{(measurements?.floors??[]).map(f=><option key={f.area_id} value={f.area_id}>{f.name}</option>)}</select></label>}
          {held?.on && <label className="flex items-center gap-1 text-micro text-muted-foreground">
            {t('accessory.qty')}
            <NumberField className={`${inputCls} w-20`} value={held.quantity} onChange={(n) => write(kind, {quantity: n,quantity_source:'manual'})} />
            {held.unit || chosenType?.unit || ''}
          </label>}
          {held?.on&&(kind==='scotia'||kind==='smoothedge'||kind==='trims')&&<span className="text-micro text-muted-foreground">{t('accessory.measuredUnit')}</span>}
          {/* WHAT IT COSTS US — staff only, and the reason a tick here is not free. */}
          {held?.on && variant?.cost != null && <span className="text-micro text-muted-foreground">
            {kind==='scotia'||kind==='smoothedge'||kind==='trims'?t('accessory.stockPieceCost',{cost:money(variant.cost),length:variant.length_mm?variant.length_mm/1000:'—'}):t('accessory.carried', {cost: money(variant.cost), unit: variant.unit ?? chosenType?.unit ?? ''})}
          </span>}
          {held && <button type="button" className="text-micro text-muted-foreground underline" onClick={() => write(kind, null)}>{t('accessory.clear')}</button>}
        </div>;
      })}
      {carpet&&<div className="rounded border bg-background p-2"><strong className="text-sm">{t('accessory.carpet.trims')}</strong>
        <p className="text-micro text-muted-foreground">{t('accessory.carpet.trimRateNeeded')}</p>
        {(['Naplock','Ripple Bar'] as const).map(name=>{const held=manualTrim(name);return <div key={name} className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <label className="flex min-w-[130px] items-center gap-2"><input type="checkbox" checked={!!held} onChange={e=>setManualTrim(name,e.target.checked)}/>{name}</label>
          {held&&<><label>{t('accessory.qty')} <NumberField className={`${inputCls} w-20`} value={held.quantity} onChange={n=>patchManualTrim(name,{quantity:n,quantity_source:'manual'})}/> LM</label>
            <label>{t('accessory.trim.pricing')} <select aria-label={`${name} ${t('accessory.trim.pricing')}`} className={inputCls} value={held.bound_to?'included':'separate'} onChange={e=>patchManualTrim(name,manualTrimChargingPatch(e.target.value as 'included'|'separate',product.id,held.rate))}>
              <option value="included">{t('accessory.trim.included')}</option><option value="separate">{t('accessory.trim.separate')}</option>
            </select></label>
            {!held.bound_to&&<label>{t('work.rate')} <NumberField className={`${inputCls} w-24`} value={held.rate} placeholder={t('work.rateUnset')} onChange={n=>patchManualTrim(name,{rate:n})}/> /LM</label>}</>}
        </div>;})}
      </div>}
    </div>
  </div>;
}
